import React, { useRef } from 'react';
import { RestoredAdModalSheet } from '@/components/Ad/RestoredAdModalSheet';
import { AdStatus } from '@/types';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';
import { StatusCard } from '@/components/Ad/Status/StatusCard';
import { PaymentFlow } from '@/components/Ad/Status/PaymentFlow';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { QUERY } from '@/constants/common';
import {
  PayByBalancePopupRef,
  PayByBalancePopup,
} from '@/components/Ad/Status/PayByBalancePopup';
import {
  ProlongByBalancePopup,
  ProlongByBalancePopupRef,
} from '@/components/Ad/Status/ProlongByBalancePopup';
import { useUrlAndHashFromPath } from '@/hooks/useUrlAndHashFromPath';

function isCurrentBrowserPath(path: string) {
  if (typeof window === 'undefined') return false;

  return window.location.pathname === path;
}

interface StatusProps {
  status: AdStatus;
  isAuthor: boolean;
}

export const Status: React.FC<StatusProps> = ({ status, isAuthor }) => {
  const { clearUrl } = useUrlAndHashFromPath();
  const [isPaymentFlowOpen, paymentFlowToggle] = useToggleOpenState(
    QUERY.awaitingPayment
  );

  const [isRestoredModalSheetOpen, toggleRestoredModalSheet] =
    useToggleOpenState(isAuthor ? QUERY.paidServices : false);
  const payByBalancePopupRef = useRef<PayByBalancePopupRef>(null);
  const prolongByBalancePopupRef = useRef<ProlongByBalancePopupRef>(null);

  const handlePayByBalancePopupOpen = () => {
    payByBalancePopupRef.current?.toggle();
  };

  const handleProlongByBalancePopupOpen = () => {
    prolongByBalancePopupRef.current?.toggle();
  };

  function handleRestoreSuccess() {
    if (!isCurrentBrowserPath(clearUrl)) return;

    toggleRestoredModalSheet();
  }

  return (
    <>
      <StatusCard
        status={status}
        isAuthor={isAuthor}
        onSuccess={handleRestoreSuccess}
        paymentFlowToggle={paymentFlowToggle}
        payByBalancePopupToggle={handlePayByBalancePopupOpen}
        prolongByBalancePopupToggle={handleProlongByBalancePopupOpen}
      />

      <PaymentFlow isOpen={isPaymentFlowOpen} toggle={paymentFlowToggle} />

      <PayByBalancePopup
        ref={payByBalancePopupRef}
        paymentFlowToggle={paymentFlowToggle}
        dataTestId={AdPageTestIds.statusBanner.payByBalancePopup}
      />

      <RestoredAdModalSheet
        isOpen={isRestoredModalSheetOpen}
        toggle={toggleRestoredModalSheet}
      />

      <ProlongByBalancePopup
        ref={prolongByBalancePopupRef}
        onSuccess={handleRestoreSuccess}
        dataTestId={AdPageTestIds.statusBanner.prolongByBalancePopup}
      />
    </>
  );
};
